import React, { useState, ChangeEvent, useEffect } from 'react';
import { Button, Typography, Box, Stack, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { theme } from '@/theme/theme';
import { FileUploadStatus } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { abyssColors } from '@/theme/colors';
import { GenericDialog } from '@/components/shared/GenericDialog';

// File validation constants
const ALLOWED_EXTENSIONS = ['png', 'jpeg', 'pdf', 'mp4', 'jpg'] as const;
const MAX_FILENAME_LENGTH = 250;
const MAX_FILE_SIZES: Record<(typeof ALLOWED_EXTENSIONS)[number], number> = {
  png: 10 * 1024 * 1024,
  jpeg: 10 * 1024 * 1024,
  jpg: 10 * 1024 * 1024,
  pdf: 80 * 1024 * 1024,
  mp4: 2 * 1024 * 1024 * 1024,
};

export type KpiAttachment = {
  id?: string;
  name: string;
  type: string;
  base64?: string;
  isNew: boolean;
  size?: number;
  file?: File;
  url?: string;
  resourcePath?: string;
  uploadStatus?: FileUploadStatus;
  uuid?: string;
};

type Props = {
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  attachments: KpiAttachment[];
  setAttachments: React.Dispatch<React.SetStateAction<KpiAttachment[]>>;
  onUpload: (attachments: KpiAttachment[]) => Promise<void>;
};

interface ValidationResult {
  validFiles: File[];
  fileErrors: string[];
}

const validateFiles = (files: File[]): ValidationResult => {
  const fileErrors: string[] = [];
  const validFiles: File[] = [];

  files.forEach((file) => {
    const currentFileErrors: string[] = [];

    // Check filename length
    if (file.name.length > MAX_FILENAME_LENGTH) {
      currentFileErrors.push(
        `Filename "${file.name}" is too long. Maximum length is ${MAX_FILENAME_LENGTH} characters.`
      );
    }

    // Check file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (
      !extension ||
      !ALLOWED_EXTENSIONS.includes(extension as (typeof ALLOWED_EXTENSIONS)[number])
    ) {
      currentFileErrors.push(
        `File extension for "${file.name}" not allowed. Allowed extensions are: ${ALLOWED_EXTENSIONS.join(', ')}.`
      );
    }

    // Check file size
    const maxFileSize = MAX_FILE_SIZES[extension as (typeof ALLOWED_EXTENSIONS)[number]] || 0;
    if (file.size > maxFileSize) {
      currentFileErrors.push(
        `File size of "${file.name}" exceeds the limit for ${extension} files. Maximum size is ${maxFileSize / (1024 * 1024)} MB.`
      );
    }

    if (currentFileErrors.length > 0) {
      fileErrors.push(...currentFileErrors);
    } else {
      validFiles.push(file);
    }
  });

  return { validFiles, fileErrors };
};

const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () =>
      reject(new Error(reader.error?.message || 'File reading failed'))
    );
  });

export const UploadAttachmentsDialog = ({
  openDialog,
  setOpenDialog,
  attachments,
  setAttachments,
  onUpload,
}: Props) => {
  const [uploadedAttachments, setUploadedAttachments] = useState<KpiAttachment[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    if (openDialog) {
      setUploadedAttachments([]);
    } else {
      setUploadedAttachments(attachments);
    }
  }, [openDialog, attachments]);

  const handleAddAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (uploadedAttachments.length + (files?.length || 0) > MAX_FILE_UPLOAD_LIMIT) {
      showSnackBar(
        `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
        'error'
      );
      return;
    }

    if (files && files.length > 0) {
      const fileArray = [...files];
      const { validFiles, fileErrors } = validateFiles(fileArray);

      if (fileErrors.length > 0) {
        fileErrors.forEach((error) => showSnackBar(error, 'error'));
        return;
      }

      const duplicateFiles = validFiles.filter((file) =>
        uploadedAttachments.some((att) => att.name === file.name.replaceAll(/[^\w.-]/g, '_'))
      );

      if (duplicateFiles.length > 0) {
        showSnackBar(
          `Duplicate file names found: ${duplicateFiles.map((file) => file.name).join(', ')}`,
          'error'
        );
        return;
      }

      const attachmentsPayload: KpiAttachment[] = await Promise.all(
        validFiles.map(async (file, index) => {
          const isImageOrVideo = file.type.startsWith('image/');
          const base64 = isImageOrVideo ? await convertToBase64(file) : '';
          return {
            uuid: `file-${Date.now()}-${index}`,
            name: file.name,
            type: file.type,
            base64: base64,
            isNew: true,
            size: file.size,
            file: file,
            uploadStatus: FileUploadStatus.Inprogress,
          };
        })
      );

      setUploadedAttachments((previousAttachments) => [
        ...previousAttachments,
        ...attachmentsPayload,
      ]);
    }

    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = uploadedAttachments.filter((_, subIndex) => subIndex !== index);
    setUploadedAttachments(newAttachments);
  };

  const handleClose = () => {
    if (isUploading) return;

    setOpenDialog(false);
    setUploadedAttachments([]);
  };

  const handleUpload = async () => {
    const attachmentsToBeUploaded = uploadedAttachments.filter((file) => file.isNew);

    if (attachmentsToBeUploaded.length > 0) {
      setIsUploading(true);
      try {
        await onUpload(attachmentsToBeUploaded);
        // Clear the uploaded attachments after successful upload
        setUploadedAttachments([]);
        setAttachments([]);
        setOpenDialog(false);
      } catch (error) {
        // Keep attachments in case of error so user can retry
        console.error('Upload failed:', error);
      } finally {
        setIsUploading(false);
      }
    } else {
      setOpenDialog(false);
      setUploadedAttachments([]);
    }
  };

  const dialogContent = (
    <Box>
      <Box sx={{ pt: 2 }}>
        <Typography variant="caption" sx={{ color: theme.palette.primary.dark }}>
          Attachments{'  '}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          (max {MAX_FILE_UPLOAD_LIMIT} uploads)
        </Typography>
      </Box>
      <Grid container spacing={2.5} sx={{ my: 1 }}>
        <Grid>
          <label htmlFor="kpi-file-upload">
            <input
              accept="image/jpeg,image/png,application/pdf,video/mp4"
              style={{ display: 'none' }}
              id="kpi-file-upload"
              multiple
              type="file"
              onChange={handleAddAttachment}
            />
            <Box
              sx={{
                border: `0.1rem solid ${abyssColors.primary[200]}`,
                borderRadius: '0.4rem',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '10rem',
                height: '10rem',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: abyssColors.primary[50],
                },
              }}
            >
              <ArrowUpwardIcon style={{ color: theme.palette.text.secondary }} />
              <Typography
                variant="caption"
                style={{ color: theme.palette.text.secondary, marginTop: 8 }}
              >
                Upload
              </Typography>
            </Box>
          </label>
        </Grid>
        {uploadedAttachments
          ?.filter((attachment) => !!attachment)
          .map((attachment, index) => (
            <Grid key={attachment.id || attachment.uuid || `attachment-${index}`}>
              <FileDisplay
                collectionName="kpi"
                selectedAttachment={{
                  ...attachment,
                }}
                selectedAttachmentIndex={index}
                handleRemoveAttachment={handleRemoveAttachment}
                setIsUpdating={() => {}}
                isUploadDialog={true}
                showRemoveIcon
              />
            </Grid>
          ))}
      </Grid>
    </Box>
  );

  const dialogActions = (
    <Stack direction="row" justifyContent="flex-end" spacing={2}>
      <Button
        onClick={handleClose}
        color="primary"
        variant="outlined"
        sx={{ borderRadius: 2 }}
        disabled={isUploading}
      >
        Cancel
      </Button>
      <Button
        onClick={handleUpload}
        color="primary"
        variant="contained"
        sx={{ borderRadius: 2, background: theme.palette.primary.dark }}
        disabled={uploadedAttachments.filter((file) => file.isNew).length === 0 || isUploading}
        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </Button>
    </Stack>
  );

  return (
    <GenericDialog
      open={openDialog}
      onClose={handleClose}
      title="Upload Attachments"
      content={dialogContent}
      actions={dialogActions}
      maxWidth="sm"
      paperStyles={{ borderRadius: 2 }}
      actionsStyles={{ justifyContent: 'flex-end', px: 2 }}
      id="upload-attachments-dialog"
    />
  );
};
